import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Trash2, Check, X, Mail, Phone, Users, MapPin, Calendar, GraduationCap, Building } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  pending: 'En attente',
  approved: 'Approuvée',
  rejected: 'Rejetée',
  completed: 'Réalisée'
};

const STATUS_COLORS = {
  pending: 'text-orange-600 bg-orange-100',
  approved: 'text-[#4A6B48] bg-[#4A6B48]/10',
  rejected: 'text-red-600 bg-red-100',
  completed: 'text-blue-600 bg-blue-100'
};

export default function TrainingManager() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [notesById, setNotesById] = useState({});

  const fetchRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/training-requests`, { withCredentials: true });
      setRequests(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/training-requests/${id}`, {
        status,
        admin_notes: notesById[id] || ''
      }, { withCredentials: true });
      toast.success(`Demande ${STATUS_LABELS[status].toLowerCase()}`);
      fetchRequests();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    try {
      await axios.delete(`${API}/admin/training-requests/${id}`, { withCredentials: true });
      toast.success('Supprimée');
      fetchRequests();
    } catch (e) { toast.error('Erreur'); }
  };

  const filtered = requests.filter(r => filter === 'all' || r.status === filter);
  const counts = {
    all: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
    completed: requests.filter(r => r.status === 'completed').length,
  };

  return (
    <div data-testid="training-manager">
      <h2 className="text-2xl font-bold text-[#2E332D] mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
        Demandes de Formation FPT ({requests.length})
      </h2>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: 'all', label: 'Toutes' },
          { id: 'pending', label: 'En attente' },
          { id: 'approved', label: 'Approuvées' },
          { id: 'completed', label: 'Réalisées' },
          { id: 'rejected', label: 'Rejetées' }
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
              filter === f.id ? 'bg-[#4A6B48] text-white' : 'bg-white border-2 border-[#E5E3DB] text-[#5C665A]'
            }`}
          >
            {f.label} ({counts[f.id]})
          </button>
        ))}
      </div>

      {loading && <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6B48]"></div></div>}

      {!loading && filtered.length === 0 && (
        <p className="text-center text-[#5C665A] py-8">Aucune demande dans cette catégorie.</p>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-4">
          {filtered.map((r) => (
            <div key={r.request_id} className="card" data-testid={`training-item-${r.request_id}`}>
              <div className="flex justify-between items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[300px]">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <span className={`text-xs font-bold px-2 py-1 rounded ${STATUS_COLORS[r.status]}`}>
                      {STATUS_LABELS[r.status]}
                    </span>
                    <span className="text-xs text-[#5C665A]">
                      Reçue le {new Date(r.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-[#2E332D] mb-1 flex items-center gap-2">
                    <GraduationCap size={18} className="text-[#4A6B48]" />
                    {r.theme}
                  </h3>
                  
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-[#5C665A]">
                    <div className="flex items-center gap-2"><strong>De:</strong> {r.requester_name}</div>
                    <div className="flex items-center gap-2"><Mail size={14} />{r.requester_email}</div>
                    {r.requester_phone && <div className="flex items-center gap-2"><Phone size={14} />{r.requester_phone}</div>}
                    {r.organization && <div className="flex items-center gap-2"><Building size={14} />{r.organization}</div>}
                    <div className="flex items-center gap-2"><Users size={14} />{r.number_of_participants} participants</div>
                    <div className="flex items-center gap-2"><MapPin size={14} />{r.location}</div>
                    <div className="flex items-center gap-2"><Calendar size={14} />Date souhaitée: {new Date(r.requested_date).toLocaleDateString('fr-FR')}</div>
                  </div>

                  <div className="mt-4 p-3 bg-[#F9F8F6] rounded-lg">
                    <p className="text-sm text-[#2E332D] whitespace-pre-wrap">{r.description}</p>
                  </div>

                  {r.admin_notes && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs font-bold text-blue-700 mb-1">Notes admin :</p>
                      <p className="text-sm text-[#2E332D]">{r.admin_notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {r.status === 'pending' && (
                <div className="mt-4 pt-4 border-t border-[#E5E3DB] space-y-2">
                  <textarea
                    placeholder="Notes / commentaire (optionnel, visible uniquement par l'admin)"
                    rows={2}
                    value={notesById[r.request_id] || ''}
                    onChange={(e) => setNotesById({ ...notesById, [r.request_id]: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm"
                  />
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => updateStatus(r.request_id, 'approved')} className="px-4 py-2 bg-[#4A6B48] text-white rounded-lg text-sm font-bold hover:bg-[#3B5539] inline-flex items-center gap-1">
                      <Check size={16} /> Approuver
                    </button>
                    <button onClick={() => updateStatus(r.request_id, 'rejected')} className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-bold hover:bg-red-600 inline-flex items-center gap-1">
                      <X size={16} /> Rejeter
                    </button>
                    <button onClick={() => handleDelete(r.request_id)} className="px-4 py-2 border border-[#E5E3DB] text-[#5C665A] rounded-lg text-sm hover:bg-gray-50 inline-flex items-center gap-1">
                      <Trash2 size={16} /> Supprimer
                    </button>
                  </div>
                </div>
              )}

              {r.status === 'approved' && (
                <div className="mt-4 pt-4 border-t border-[#E5E3DB] flex gap-2 flex-wrap">
                  <button onClick={() => updateStatus(r.request_id, 'completed')} className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-bold hover:bg-blue-600">
                    Marquer comme réalisée
                  </button>
                  <button onClick={() => handleDelete(r.request_id)} className="px-4 py-2 border border-[#E5E3DB] text-[#5C665A] rounded-lg text-sm">
                    <Trash2 size={16} className="inline" />
                  </button>
                </div>
              )}

              {(r.status === 'rejected' || r.status === 'completed') && (
                <div className="mt-4 pt-4 border-t border-[#E5E3DB]">
                  <button onClick={() => handleDelete(r.request_id)} className="px-4 py-2 border border-[#E5E3DB] text-[#5C665A] rounded-lg text-sm inline-flex items-center gap-1">
                    <Trash2 size={16} /> Supprimer
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
